import { useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { QrCode, Lightbulb, Info, CheckCircle2 } from 'lucide-react';
import { CameraPreview } from '@/components/shared/camera/CameraPreview';
import type { QRCodeScanner } from '@/lib/qr/QRCodeScanner';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card/Card';
import { Callout } from '@/components/ui/callout';

interface CameraScannerCardProps {
  scanner: QRCodeScanner;
  detectedId: string | null;
  onDetected: (toolId: string) => void;
  isActive: boolean;
  isLoading?: boolean;
}

export function CameraScannerCard({
  scanner,
  detectedId,
  onDetected,
  isActive,
  isLoading = false,
}: CameraScannerCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isActive || detectedId) return;

    let canceled = false;
    let timer: number | null = null;

    const scanLoop = async () => {
      if (canceled || detectedId) return;

      const canvas = canvasRef.current;
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        const id = await scanner.scan(canvas);
        if (canceled) return;
        if (id && !detectedId) {
          onDetected(id);
          return;
        }
      }
      timer = window.setTimeout(scanLoop, 100);
    };

    scanLoop();

    return () => {
      canceled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [scanner, detectedId, isActive, onDetected]);

  const isDetected = Boolean(detectedId);

  if (isLoading) {
    return (
      <Card as="section" padding="lg" className="flex flex-col gap-6">
        <header className="flex items-start gap-3">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
          </div>
        </header>
        <Skeleton className="aspect-video rounded-2xl" />
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" padding="lg" className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
          <QrCode className="h-5 w-5" />
        </span>
        <div className="space-y-1">
          <h2 className="section-title">{t('traceability.scanPage.camera.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('traceability.scanPage.camera.description')}
          </p>
        </div>
      </header>

      <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/90 shadow-inner">
        <CameraPreview canvasRef={canvasRef} detectedId={detectedId} className="rounded-none" />
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 m-4 flex items-center justify-between rounded-xl border bg-card/80 px-4 py-3 text-sm shadow-sm backdrop-blur-sm',
            isDetected ? 'border-primary/30' : 'border-border'
          )}
        >
          <div className="flex items-center gap-2">
            {isDetected ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="font-medium text-primary">
                  {t('traceability.scanPage.camera.detected')}
                </span>
              </>
            ) : (
              <>
                <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                <span className="text-muted-foreground">
                  {t('traceability.scanPage.camera.scanning')}
                </span>
              </>
            )}
          </div>
          {detectedId && (
            <span className="truncate font-mono text-xs font-semibold text-primary">
              {detectedId}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Callout tone="neutral" icon={Lightbulb}>
          {t('traceability.scanPage.camera.tips.lighting')}
        </Callout>
        <Callout tone="neutral" icon={Info}>
          {t('traceability.scanPage.camera.tips.position')}
        </Callout>
      </div>
    </Card>
  );
}
